import { Component } from '@theme/component';
import { onAnimationEnd } from '@theme/utilities';
import { ThemeEvents, CartUpdateEvent } from '@theme/events';

/**
 * A custom element that displays a cart icon.
 *
 * @typedef {object} Refs
 * @property {HTMLElement} cartBubble - The cart bubble element.
 * @property {HTMLElement} cartBubbleText - The cart bubble text element.
 * @property {HTMLElement} cartBubbleCount - The cart bubble count element.
 *
 * @extends {Component<Refs>}
 */
class CartIcon extends Component {
  requiredRefs = ['cartBubble', 'cartBubbleText', 'cartBubbleCount'];


  /** @type {number} */
  get currentCartCount() {
    return parseInt(this.refs.cartBubbleCount.textContent ?? '0', 10);
  }

  set currentCartCount(value) {
    this.refs.cartBubbleCount.textContent = value < 100 ? String(value) : '';
  }

  connectedCallback() {
    super.connectedCallback();

    document.addEventListener(ThemeEvents.cartUpdate, this.onCartUpdate);
    // VINEY (T672 rule 3): the reconciliation read after a stale answer (component-cart-items.js)
    document.addEventListener('vn:cart-reconcile', this.#vnOnReconcile);
    window.addEventListener('pageshow', this.onPageShow);
    this.ensureCartBubbleIsCorrect();
  }

  disconnectedCallback() {
    super.disconnectedCallback();

    document.removeEventListener(ThemeEvents.cartUpdate, this.onCartUpdate);
    document.removeEventListener('vn:cart-reconcile', this.#vnOnReconcile);
    window.removeEventListener('pageshow', this.onPageShow);
  }

  /**
   * Handles the page show event when the page is restored from cache.
   * @param {PageTransitionEvent} event - The page show event.
   */
  onPageShow = (event) => {
    if (event.persisted) {
      this.ensureCartBubbleIsCorrect();
    }
  };

  /**
   * Handles the cart update event.
   * @param {CartUpdateEvent} event - The cart update event.
   */
  onCartUpdate = async (event) => {
    const itemCount = event.detail.data?.itemCount ?? 0;
    const comingFromProductForm = event.detail.data?.source === 'product-form-component';

    // VINEY (T672 rule 3, live bug 2): the count comes from the cart itself whenever the event carries
    // it (the product form now sends the cart it read back, after a success and after a failure), so a
    // refused add no longer raises the count and a partial success shows what really went in. A failure
    // without a readable cart leaves the count alone. The bump is drawn by assets/vn-motion.js (the
    // berry lands first), so Horizon's own bubble animation is skipped for adds.
    const cartCount = /** @type {any} */ (event.detail.resource)?.item_count;
    if (typeof cartCount === 'number') {
      // a cart read older than the newest cart shown is skipped (product-form.js numbers its reads and
      // keeps window.__vnCartFresh); a cart from elsewhere (a line changed in the drawer) moves the line
      // past every read already sent, so a read still on its way is skipped too
      const w = /** @type {any} */ (window);
      const read = /** @type {any} */ (event.detail.resource).__vnRead;
      if (read !== undefined && read < (w.__vnCartFresh || 0)) {
        this.#vnReconcileSoon();
        return;
      }
      w.__vnCartFresh = Math.max(w.__vnCartFresh || 0, read ?? (w.__vnCartRead || 0) + 0.5);
      this.renderCartBubble(cartCount, false, !comingFromProductForm);
      return;
    }
    if (event.detail.data?.didError) return;

    // VINEY (T672 rule 3): an add whose cart could not be read is not added on top of a count that may
    // already include it (or a newer cart); the cart is read again, numbered, and shown if still newest
    if (comingFromProductForm) {
      this.#vnReadCart();
      return;
    }

    this.renderCartBubble(itemCount, comingFromProductForm);
  };

  #vnOnReconcile = (/** @type {any} */ event) => this.#vnReadCart(event.vnGen);

  /** VINEY: asks for the shared reconciliation (the same debounce as component-cart-items.js) */
  #vnReconcileSoon() {
    const w = /** @type {any} */ (window);
    clearTimeout(w.__vnReconcileTimer);
    w.__vnReconcileTimer = setTimeout(() => {
      const e = /** @type {any} */ (new CustomEvent('vn:cart-reconcile'));
      e.vnGen = w.__vnCartRead = (w.__vnCartRead || 0) + 1;
      document.dispatchEvent(e);
    }, 300);
  }

  /**
   * VINEY (T672 rule 3): reads the cart and shows its count, unless a newer cart is shown by then
   * @param {number} [gen] - the reconciliation's number, or a new one
   */
  #vnReadCart = async (gen) => {
    const w = /** @type {any} */ (window);
    const read = gen ?? (w.__vnCartRead = (w.__vnCartRead || 0) + 1);
    try {
      const cart = await (await fetch('/cart.js')).json();
      if (read < (w.__vnCartFresh || 0) || typeof cart?.item_count !== 'number') return;
      w.__vnCartFresh = Math.max(w.__vnCartFresh || 0, read);
      this.renderCartBubble(cart.item_count, false, false);
    } catch {
      // the count stays as it was
    }
  };

  /**
   * Renders the cart bubble.
   * @param {number} itemCount - The number of items in the cart.
   * @param {boolean} comingFromProductForm - Whether the cart update is coming from the product form.
   */
  renderCartBubble = async (itemCount, comingFromProductForm, animate = true) => {
    // If the cart update is coming from the product form, we add to the current cart count, otherwise we set the new cart count

    this.refs.cartBubbleCount.classList.toggle('hidden', itemCount === 0);
    this.refs.cartBubble.classList.toggle('visually-hidden', itemCount === 0);

    this.currentCartCount = comingFromProductForm ? this.currentCartCount + itemCount : itemCount;

    this.classList.toggle('header-actions__cart-icon--has-cart', itemCount > 0);

    sessionStorage.setItem(
      'cart-count',
      JSON.stringify({
        value: String(this.currentCartCount),
        timestamp: Date.now(),
      })
    );

    if (!animate || itemCount === 0) return;

    // Ensure element is visible before starting animation
    // Use requestAnimationFrame to ensure the browser sees the state change
    await new Promise((resolve) => requestAnimationFrame(resolve));

    this.refs.cartBubble.classList.add('cart-bubble--animating');
    await onAnimationEnd(this.refs.cartBubbleText);

    this.refs.cartBubble.classList.remove('cart-bubble--animating');
  };

  /**
   * Checks if the cart count is correct.
   */
  ensureCartBubbleIsCorrect = () => {
    // Ensure refs are available
    if (!this.refs.cartBubbleCount) return;

    const sessionStorageCount = sessionStorage.getItem('cart-count');

    // If no session storage data, nothing to check
    if (sessionStorageCount === null) return;

    const visibleCount = this.refs.cartBubbleCount.textContent;

    try {
      const { value, timestamp } = JSON.parse(sessionStorageCount);

      // Check if the stored count matches what's visible
      if (value === visibleCount) return;

      // Only update if timestamp is recent (within 10 seconds)
      if (Date.now() - timestamp < 10000) {
        const count = parseInt(value, 10);

        if (count >= 0) {
          this.renderCartBubble(count, false, false);
        }
      }
    } catch (_) {
      // no-op
    }
  };
}

if (!customElements.get('cart-icon')) {
  customElements.define('cart-icon', CartIcon);
}
